/**
 * Workspace Repository — CRUD untuk tabel workspace_cache.
 *
 * Workspace adalah unit organisasi tertinggi yang bisa diakses user.
 * Berbeda dari activity_cache, workspace_cache tidak memiliki kolom
 * created_at dan deleted_at (hanya updated_at).
 *
 * Acuan: docs/04-Database.md §6 (workspace_cache), docs/02-UseCase.md UC-02.
 */

import type { SqlValue } from 'sql.js';
import { BaseRepository } from '../repository';
import type { WorkspaceCache, SyncStatus } from '../types';

/** Input untuk membuat workspace baru. */
export interface CreateWorkspaceInput {
  tenant_id: string;
  owner_id: string;
  name: string;
  description?: string | null;
  settings?: string;
}

/** Input untuk update workspace. */
export interface UpdateWorkspaceInput {
  name?: string;
  description?: string | null;
  settings?: string;
}

export class WorkspaceRepository extends BaseRepository<WorkspaceCache> {
  protected readonly tableName = 'workspace_cache';

  protected mapRow(row: Record<string, unknown>): WorkspaceCache {
    return row as unknown as WorkspaceCache;
  }

  // -------------------------------------------------------------------------
  // Create
  // -------------------------------------------------------------------------

  async create(data: CreateWorkspaceInput): Promise<WorkspaceCache> {
    const id = this.generateId();
    const now = this.now();

    this.insertRow({
      id,
      tenant_id: data.tenant_id,
      owner_id: data.owner_id,
      name: data.name,
      description: data.description ?? null,
      settings: data.settings ?? '{}',
      sync_status: 'pending',
      last_synced_at: null,
      updated_at: now,
    });

    const created = await this.findById(id);
    if (!created) throw new Error(`Failed to create workspace: ${id}`);
    return created;
  }

  // -------------------------------------------------------------------------
  // Update
  // -------------------------------------------------------------------------

  async update(id: string, changes: UpdateWorkspaceInput): Promise<WorkspaceCache | null> {
    const updateData: Record<string, SqlValue> = {
      updated_at: this.now(),
      sync_status: 'pending',
    };

    if (changes.name !== undefined) updateData.name = changes.name;
    if (changes.description !== undefined) updateData.description = changes.description;
    if (changes.settings !== undefined) updateData.settings = changes.settings;

    this.updateRow(id, updateData);
    return this.findById(id);
  }

  // -------------------------------------------------------------------------
  // Specialized Queries
  // -------------------------------------------------------------------------

  /** Ambil semua workspace milik tenant tertentu. */
  async findByTenant(tenantId: string): Promise<WorkspaceCache[]> {
    const sql = `SELECT * FROM ${this.tableName} WHERE tenant_id = ? ORDER BY updated_at DESC`;
    const rows = this.db.queryAll<Record<string, unknown>>(sql, [tenantId]);
    return rows.map((row) => this.mapRow(row));
  }

  /** Ambil workspace milik user tertentu (sebagai owner). */
  async findByOwner(ownerId: string): Promise<WorkspaceCache[]> {
    const sql = `SELECT * FROM ${this.tableName} WHERE owner_id = ? ORDER BY updated_at DESC`;
    const rows = this.db.queryAll<Record<string, unknown>>(sql, [ownerId]);
    return rows.map((row) => this.mapRow(row));
  }

  /** Ambil workspace dengan sync_status tertentu (untuk sync engine). */
  async findBySyncStatus(status: SyncStatus): Promise<WorkspaceCache[]> {
    const sql = `SELECT * FROM ${this.tableName} WHERE sync_status = ?`;
    const rows = this.db.queryAll<Record<string, unknown>>(sql, [status]);
    return rows.map((row) => this.mapRow(row));
  }

  /** Tandai workspace sebagai tersinkron. */
  async markSynced(id: string): Promise<void> {
    this.updateRow(id, {
      sync_status: 'synced',
      last_synced_at: this.now(),
    });
  }

  // -------------------------------------------------------------------------
  // Delete (hard delete — workspace_cache tidak punya deleted_at)
  // -------------------------------------------------------------------------

  /**
   * Hapus workspace permanen dari cache lokal.
   * Catatan: ini hanya menghapus dari cache, bukan dari cloud.
   * Cloud deletion ditangani oleh sync engine (S14).
   */
  async delete(id: string): Promise<void> {
    this.db.run(`DELETE FROM ${this.tableName} WHERE id = ?`, [id]);
  }
}
